import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../reducers';

export interface PillsByFunnelEntity {
  steps: {
    [stepId: string]: number;
  };
  funnels: {
    [funnelId: string]: number;
  };
  objectType: {
    [objectType: string]: number;
  };
  recordType: {
    [recordType: string]: number;
  };
}

export interface PillsMap {
  sweepAutomations: PillsByFunnelEntity;
  alerts: PillsByFunnelEntity;
  assignments: PillsByFunnelEntity;
  scheduledAssignments: PillsByFunnelEntity;
  sdfcAutomations: PillsByFunnelEntity;
  validationRules: PillsByFunnelEntity;
  apex: PillsByFunnelEntity;
  funnelSfcAutomations: PillsByFunnelEntity;
  funnelValidationRules: PillsByFunnelEntity;
  funnelApex: PillsByFunnelEntity;
  dedupe: PillsByFunnelEntity;
  matching: PillsByFunnelEntity;
  hubspotEnrollments: PillsByFunnelEntity;
}

export interface CanvasPillsState {
  pillsMap: {
    [crmOrgId: string]: PillsMap;
  };
}

const EMPTY_PILLS_MAP: PillsMap = {
  sweepAutomations: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  alerts: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  assignments: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  scheduledAssignments: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  sdfcAutomations: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  validationRules: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  apex: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  funnelSfcAutomations: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  funnelValidationRules: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  funnelApex: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  dedupe: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  matching: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  hubspotEnrollments: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
};

const initialState: CanvasPillsState = {
  pillsMap: {},
};

export const canvasPillsSlice = createSlice({
  name: 'canvasPills',
  initialState,
  reducers: {
    setAutomationsPills: (
      state,
      action: PayloadAction<{ crmOrgId: string; pills: PillsByFunnelEntity }>,
    ) => {
      if (!state.pillsMap[action.payload.crmOrgId]) {
        state.pillsMap[action.payload.crmOrgId] = { ...EMPTY_PILLS_MAP };
      }
      state.pillsMap[action.payload.crmOrgId].sweepAutomations = action.payload.pills;
    },
    setAlertsPills: (
      state,
      action: PayloadAction<{ crmOrgId: string; pills: PillsByFunnelEntity }>,
    ) => {
      if (!state.pillsMap[action.payload.crmOrgId]) {
        state.pillsMap[action.payload.crmOrgId] = { ...EMPTY_PILLS_MAP };
      }
      state.pillsMap[action.payload.crmOrgId].alerts = action.payload.pills;
    },
    setAssignmentPills: (
      state,
      action: PayloadAction<{ crmOrgId: string; pills: PillsByFunnelEntity }>,
    ) => {
      if (!state.pillsMap[action.payload.crmOrgId]) {
        state.pillsMap[action.payload.crmOrgId] = { ...EMPTY_PILLS_MAP };
      }
      state.pillsMap[action.payload.crmOrgId].assignments = action.payload.pills;
    },
    setScheduledAssignmentPills: (
      state,
      action: PayloadAction<{ crmOrgId: string; pills: PillsByFunnelEntity }>,
    ) => {
      if (!state.pillsMap[action.payload.crmOrgId]) {
        state.pillsMap[action.payload.crmOrgId] = { ...EMPTY_PILLS_MAP };
      }
      state.pillsMap[action.payload.crmOrgId].scheduledAssignments = action.payload.pills;
    },
    setDedupePills: (
      state,
      action: PayloadAction<{ crmOrgId: string; pills: PillsByFunnelEntity }>,
    ) => {
      if (!state.pillsMap[action.payload.crmOrgId]) {
        state.pillsMap[action.payload.crmOrgId] = { ...EMPTY_PILLS_MAP };
      }
      state.pillsMap[action.payload.crmOrgId].dedupe = action.payload.pills;
    },
    setMatchingPills: (
      state,
      action: PayloadAction<{ crmOrgId: string; pills: PillsByFunnelEntity }>,
    ) => {
      if (!state.pillsMap[action.payload.crmOrgId]) {
        state.pillsMap[action.payload.crmOrgId] = { ...EMPTY_PILLS_MAP };
      }
      state.pillsMap[action.payload.crmOrgId].matching = action.payload.pills;
    },
    setDocumentationPills: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        documentation: {
          sdfcAutomations: PillsByFunnelEntity;
          validationRules: PillsByFunnelEntity;
          apex: PillsByFunnelEntity;
          funnelSfcAutomations: PillsByFunnelEntity;
          funnelValidationRules: PillsByFunnelEntity;
          funnelApex: PillsByFunnelEntity;
        };
      }>,
    ) => {
      if (!state.pillsMap[action.payload.crmOrgId]) {
        state.pillsMap[action.payload.crmOrgId] = { ...EMPTY_PILLS_MAP };
      }
      state.pillsMap[action.payload.crmOrgId].sdfcAutomations =
        action.payload.documentation.sdfcAutomations;
      state.pillsMap[action.payload.crmOrgId].validationRules =
        action.payload.documentation.validationRules;
      state.pillsMap[action.payload.crmOrgId].apex = action.payload.documentation.apex;
      state.pillsMap[action.payload.crmOrgId].funnelSfcAutomations =
        action.payload.documentation.funnelSfcAutomations;
      state.pillsMap[action.payload.crmOrgId].funnelValidationRules =
        action.payload.documentation.funnelValidationRules;
      state.pillsMap[action.payload.crmOrgId].funnelApex = action.payload.documentation.funnelApex;
    },
    setHubspotEnrollmentPills: (
      state,
      action: PayloadAction<{ crmOrgId: string; count: number }>,
    ) => {
      if (!state.pillsMap[action.payload.crmOrgId]) {
        state.pillsMap[action.payload.crmOrgId] = { ...EMPTY_PILLS_MAP };
      }
      state.pillsMap[action.payload.crmOrgId].hubspotEnrollments = {
        funnels: {
          hubspot: action.payload.count,
        },
        objectType: {},
        recordType: {},
        steps: {},
      };
    },
  },
});

export const {
  setAutomationsPills,
  setAlertsPills,
  setAssignmentPills,
  setDocumentationPills,
  setDedupePills,
  setMatchingPills,
  setScheduledAssignmentPills,
  setHubspotEnrollmentPills,
} = canvasPillsSlice.actions;

export const selectPillsMap = (state: RootState) => {
  return state.canvasPills.pillsMap;
};

export const selectPillsMapByCrmOrgId =
  (crmOrgId: string) =>
  (state: RootState): PillsMap => {
    return state.canvasPills.pillsMap[crmOrgId] || EMPTY_PILLS_MAP;
  };

export default canvasPillsSlice.reducer;
